// شرح حال — the case record's shared contract: the section whitelist, the entry
// form, and the two structured payload shapes.
//
// Both surfaces import from here. A record whose desktop tabs and mobile strip
// each declared their own section list is one where a section is reachable by tap
// on one device and by URL on neither — a defect that has already shipped once in
// this monorepo.

import React from 'react';

import {
  HISTORY_SECTIONS, SECTION_IDS, DEFAULT_SECTION, resolveSection, hasStructured,
  emptyVitals, vitalsToForm, formToVitals,
  emptyLabRow, labsToForm, formToLabs,
  DEFAULT_SOURCE, nextSortOrder, entryHasContent, groupBySection,
} from '../../lib/history.js';
import { iranDateOf, isoDayToMs } from '../../lib/iranTime.js';

// Re-exported so a surface imports the record's whole contract from one module.
export {
  HISTORY_SECTIONS, SECTION_IDS, DEFAULT_SECTION, resolveSection, hasStructured,
  groupBySection, nextSortOrder, entryHasContent,
};

/**
 * The `:section` URL whitelist.
 *
 * Identical to SECTION_IDS by construction rather than by hand. The route wrapper
 * validates the URL segment against it and falls back to `chief_complaint`, so a
 * stale or hand-edited link lands on a real section instead of on an editor for
 * one the server would reject AFTER the student had typed a history into it.
 */
export const SECTIONS = SECTION_IDS;

/**
 * `buildCaseUpdate` — the changed-fields diff `updateCase` takes.
 *
 * It lives in src/lib/caseForm.js, because src/components/common/CaseForm.jsx
 * needs the same form contract and a shared component must not import from a
 * screen. It is re-exported HERE because that is where src/graphql/documents.js
 * tells the next reader to look for it.
 */
export { buildCaseUpdate, caseToForm, emptyCaseForm, titleProblem } from '../../lib/caseForm.js';

/**
 * The section strip / rail items, with the count each section holds.
 *
 * `Icon` is the COMPONENT, not a rendered node — the same convention
 * src/constants/nav.js follows, and for the same reason: the desktop rail draws
 * it at 16px and the mobile strip at 16px inside the kit's own tab button, which
 * a shared element could not do. It also keeps this module free of JSX, so the
 * test runner (which transforms JSX by extension) can import it as the `.js` it
 * is.
 */
export function sectionItems(entries, t) {
  const grouped = groupBySection(entries);
  return HISTORY_SECTIONS.map(({ id, labelKey, Icon }) => ({
    key: id,
    label: t(labelKey),
    Icon,
    count: grouped[id].length,
  }));
}

// ── The entry form ──────────────────────────────────────────────────────────
//
// One form for all seven sections. `vitals` and `labs` additionally show their
// structured editor; the other five show prose alone. Structured data is an
// ADDITION to `body`, never a replacement — the prose is what a student actually
// writes and what carries the teaching value.

export function emptyEntryForm(section) {
  return {
    section,
    label: '',
    body: '',
    // ISO 'YYYY-MM-DD', which is the value shape every kit date component takes.
    recordedAt: '',
    vitals: emptyVitals(),
    labs: [emptyLabRow()],
  };
}

/** A stored entry → editable form values. */
export function entryToForm(entry) {
  const section = entry?.section || DEFAULT_SECTION;
  return {
    section,
    label: entry?.label || '',
    body: entry?.body || '',
    // The wire carries epoch milliseconds as a STRING; the editor edits a day.
    recordedAt: entry?.recordedAtMs ? iranDateOf(entry.recordedAtMs) : '',
    vitals: section === 'vitals' ? vitalsToForm(entry?.structured) : emptyVitals(),
    labs: section === 'labs' ? labsToForm(entry?.structured) : [emptyLabRow()],
  };
}

/**
 * The structured payload for a form, or null.
 *
 * null rather than `{}`: an empty object is a payload that says "vitals were
 * recorded and every one of them was empty", which is not what a blank form
 * means, and it would render as an empty vitals card forever.
 */
export function formToStructured(form) {
  if (form?.section === 'vitals') return formToVitals(form.vitals);
  if (form?.section === 'labs') return formToLabs(form.labs);
  return null;
}

/**
 * Form → `CaseHistoryEntryInput`, for `addCaseHistory`.
 *
 * `source` is set ONCE, here, and is not in the update input: provenance is a
 * fact about how a row came to exist, and an editable one would let a voice draft
 * become indistinguishable from a sentence the student wrote (CORE.md §6). The
 * voice flow passes `source: 'voice'`; everything a student types is `manual`.
 *
 * `recordedAtMs` rides as a STRING because GraphQL's Int is signed 32-bit and
 * epoch milliseconds (~1.7e12) overflow it — Apollo throws at serialization
 * rather than truncating, so the whole mutation would fail on a field nobody was
 * looking at.
 */
export function formToEntryInput(form, { sortOrder = 0, source = DEFAULT_SOURCE } = {}) {
  const ms = isoDayToMs(form?.recordedAt);
  return {
    section: form.section,
    label: String(form?.label ?? '').trim() || null,
    body: String(form?.body ?? '').trim(),
    structured: formToStructured(form),
    source,
    sortOrder,
    recordedAtMs: ms == null ? null : String(ms),
  };
}

/** Form → `CaseHistoryEntryUpdateInput`. `section` and `source` are absent by contract. */
export function formToEntryUpdate(form) {
  const ms = isoDayToMs(form?.recordedAt);
  return {
    label: String(form?.label ?? '').trim(),
    body: String(form?.body ?? '').trim(),
    structured: formToStructured(form),
    recordedAtMs: ms == null ? '' : String(ms),
  };
}

/** `history.entry.source.*` — «شما», «شما، از پیش‌نویس صوتی», «شما، از پیشنهاد هوش مصنوعی». */
export const sourceKey = (source) =>
  `history.entry.source.${['manual', 'voice', 'ai'].includes(source) ? source : DEFAULT_SOURCE}`;

// ── Rendering a stored structured payload ──────────────────────────────────

/**
 * The vitals payload as label/value pairs, in the order the form asks for them.
 *
 * `bp` is a STRING and the rest are numbers, which is the split the frozen schema
 * documents: blood pressure is two numbers with a slash and has no honest numeric
 * representation. Anything the student left blank is ABSENT from the payload
 * rather than null, so it simply does not appear here.
 */
export function vitalPairs(structured) {
  const src = structured && typeof structured === 'object' && !Array.isArray(structured) ? structured : {};
  return Object.entries(src)
    .filter(([, v]) => v !== '' && v != null)
    .map(([key, value]) => ({ key, value: String(value) }));
}

/** The labs payload as rows. Always an array by contract; anything else renders nothing. */
export function labPairs(structured) {
  return Array.isArray(structured) ? structured : [];
}

// ── The editor's wiring, shared by both surfaces ───────────────────────────
//
// Opening a blank entry, opening a stored one, validating it, and choosing
// between `addCaseHistory` and `updateCaseHistoryEntry` is the same sequence on a
// laptop and on a phone. Only the OVERLAY differs — a Dialog there, a BottomSheet
// here — so the overlay is what each surface owns and this is what both call.

/**
 * @param entries      the case's entries, for `nextSortOrder`
 * @param addHistory   from useCase — plural, all-or-nothing
 * @param updateEntry  from useCase
 */
export function useEntryEditor({ entries, addHistory, updateEntry }) {
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null); // the stored entry, or null for a new one
  const [form, setForm] = React.useState(() => emptyEntryForm(DEFAULT_SECTION));
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState('');

  const openNew = React.useCallback((section) => {
    setEditing(null);
    setForm(emptyEntryForm(section));
    setProblem('');
    setOpen(true);
  }, []);

  const openExisting = React.useCallback((entry) => {
    setEditing(entry);
    setForm(entryToForm(entry));
    setProblem('');
    setOpen(true);
  }, []);

  const close = React.useCallback(() => {
    if (saving) return;
    setOpen(false);
  }, [saving]);

  const change = React.useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setProblem('');
  }, []);

  /**
   * Save, or report what is missing.
   *
   * The BFF stores an entry with an empty body only when `structured` carries
   * something, so the same rule is checked here — otherwise the student writes a
   * row that renders as a blank card and can then only be deleted.
   *
   * @returns true when it was saved, so the caller can close and toast.
   */
  const save = React.useCallback(async () => {
    const candidate = { body: form.body, structured: formToStructured(form) };
    if (!entryHasContent(candidate)) {
      setProblem('history.entry.needsContent');
      return false;
    }

    setSaving(true);
    try {
      if (editing) {
        await updateEntry(editing.id, formToEntryUpdate(form));
      } else {
        const section = form.section;
        const sortOrder = nextSortOrder(groupBySection(entries)[section]);
        // Plural because the mutation is: `addCaseHistory` is all-or-nothing and
        // the voice flow submits a whole drafted history through the same call.
        await addHistory([formToEntryInput(form, { sortOrder })]);
      }
      setOpen(false);
      return true;
    } finally {
      setSaving(false);
    }
  }, [form, editing, entries, addHistory, updateEntry]);

  return {
    open, editing, form, saving,
    error: problem,
    openNew, openExisting, close, change, save,
  };
}
